import { useCallback, useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { EllipsisHorizontalIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { ParseAPI } from '../API';
import Parse, { ParseClasses } from '../api/config';
import { useAuth } from './AuthContext';
import {
    ARCHIVE_AFTER_DAYS,
    ApprovalCount,
    ApprovalRail,
    Banner,
    Button,
    Card,
    EmptyState,
    LoadingOverlay,
    PageBody,
    PageHeader,
    StatusBadge,
    cx,
    daysSinceEvent,
    deriveStatus,
    isArchived,
    roleLabel,
} from './ui';

// Tabs are the statuses people actually sort their own work by. Everything
// except "Past" hides requests whose event finished more than two weeks ago.
const FILTERS = [
    { key: 'all', label: 'All', match: (f) => !f.isArchived },
    { key: 'drafts', label: 'Drafts', match: (f) => !f.isArchived && f.status === 'Draft' },
    {
        key: 'pending',
        label: 'Awaiting approval',
        match: (f) => !f.isArchived && (f.status === 'Submitted' || f.status === 'Partially Approved'),
    },
    { key: 'approved', label: 'Approved', match: (f) => !f.isArchived && f.status === 'Fully Approved' },
    { key: 'attention', label: 'Needs attention', match: (f) => !f.isArchived && f.status === 'Denied' },
    { key: 'past', label: 'Past', match: (f) => f.isArchived },
];

const formatDate = (value) => (value ? format(new Date(value), 'MMM d, yyyy') : '—');

const formatEventDate = (value) => {
    if (!value) return null;
    // A bare YYYY-MM-DD parses as UTC and lands a day early in the US.
    const date = /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T00:00:00`) : new Date(value);
    return Number.isNaN(date.getTime()) ? null : format(date, 'EEE, MMM d, yyyy');
};

export const MyRequests = () => {
    const { user } = useAuth();
    const [forms, setForms] = useState([]);
    const [loadingList, setLoadingList] = useState(true);
    const [busy, setBusy] = useState(null);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [filter, setFilter] = useState('all');
    const [query, setQuery] = useState('');

    const navigate = useNavigate();

    const fetchForms = useCallback(async () => {
        if (!user?.email) return;

        setLoadingList(true);
        setError(null);
        try {
            const formQuery = new Parse.Query(ParseClasses.Form);
            formQuery.equalTo('owner', user.email);
            formQuery.descending('createdAt');
            const results = await formQuery.find();

            setForms(
                results.map((form) => {
                    const data = form.toJSON();
                    return {
                        id: form.id,
                        title: data.eventName || 'Untitled event',
                        createdAt: data.createdAt,
                        eventDate: data.dayDateOfEvent,
                        location: data.locationRequested,
                        status: deriveStatus(data),
                        occurrenceCount: (data.occurrenceDates || []).length,
                        isArchived: isArchived(data),
                        daysSinceEvent: daysSinceEvent(data),
                        isSaveTheDate: !!data.isSaveTheDate,
                        requiredApprovals: data.requiredApprovals || [],
                        completedApprovals: data.completedApprovals || [],
                        cancelledAt: data.cancelledAt,
                        cancellationReason: data.cancellationReason,
                    };
                })
            );
        } catch (err) {
            console.error('Could not load your requests:', err);
            setError(`Your requests could not be loaded: ${err.message}`);
        } finally {
            setLoadingList(false);
        }
    }, [user]);

    useEffect(() => {
        fetchForms();
    }, [fetchForms]);

    // Denial details are an extra round-trip each, so only fetch them for the
    // handful of forms that are actually denied.
    const [denials, setDenials] = useState({});
    useEffect(() => {
        const denied = forms.filter((f) => f.status === 'Denied' && !denials[f.id]);
        if (!denied.length) return;

        let cancelled = false;
        (async () => {
            const entries = await Promise.all(
                denied.map(async (f) => {
                    try {
                        const res = await Parse.Cloud.run('getFormApprovalStatus', { formId: f.id });
                        return [f.id, res?.denialInfo || null];
                    } catch {
                        return [f.id, null];
                    }
                })
            );
            if (!cancelled) setDenials((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
        })();

        return () => {
            cancelled = true;
        };
    }, [forms, denials]);

    const visible = useMemo(() => {
        const active = FILTERS.find((f) => f.key === filter) || FILTERS[0];
        const q = query.trim().toLowerCase();
        return forms
            .filter(active.match)
            .filter((f) => !q || f.title.toLowerCase().includes(q) || (f.location || '').toLowerCase().includes(q));
    }, [forms, filter, query]);

    const counts = useMemo(
        () => Object.fromEntries(FILTERS.map((f) => [f.key, forms.filter(f.match).length])),
        [forms]
    );

    /* ------------------------------------------------------------- actions */

    const run = async (message, fn) => {
        setBusy(message);
        setError(null);
        setNotice(null);
        try {
            await fn();
        } catch (err) {
            console.error(err);
            setError(err.message);
        } finally {
            setBusy(null);
        }
    };

    const openForm = (id) => navigate(`/home/form/${id}`);

    const downloadPdf = (form) =>
        run('Generating PDF…', async () => {
            const result = await Parse.Cloud.run('generatePDF', { formId: form.id });
            if (!result?.url) throw new Error('The server did not return a PDF.');
            window.open(result.url, '_blank', 'noopener');
        });

    const duplicate = (form) =>
        run('Copying request…', async () => {
            const parseForm = await new Parse.Query(ParseClasses.Form).get(form.id);
            const data = parseForm.toJSON();
            localStorage.setItem(
                'duplicatedFormData',
                JSON.stringify({
                    ...data,
                    dayDateOfEvent: '',
                    startTime: '',
                    endTime: '',
                    prepTime: '',
                    eventName: `${data.eventName} (copy)`,
                })
            );
            navigate('/home/form');
        });

    const convertSaveTheDate = (form) =>
        run('Building the full request…', async () => {
            const created = await ParseAPI.convertSaveTheDateToMasterCalendar(form.id);
            navigate(`/home/form/${created.id}`);
        });

    const resendApprovalEmails = (form) =>
        run('Sending approval emails…', async () => {
            const result = await Parse.Cloud.run('sendApprovalEmails', { formId: form.id });
            if (!result?.success) throw new Error(result?.message || 'The emails could not be sent.');
        });

    const remindApprovers = (form) =>
        run('Sending reminders…', async () => {
            const result = await Parse.Cloud.run('remindPendingApprovers', { formId: form.id });
            setNotice(result?.message || 'Reminders sent.');
        });

    const resubmit = (form) =>
        run('Preparing a new request…', async () => {
            const result = await Parse.Cloud.run('resubmitDeniedForm', { formId: form.id });
            if (!result?.success) throw new Error(result?.message || 'The request could not be reopened.');
            navigate(`/home/form/${result.newFormId}`);
        });

    const cancel = (form) => {
        const reason = window.prompt(`Why are you cancelling "${form.title}"? Approvers will be told.`);
        if (!reason?.trim()) return;
        return run('Cancelling request…', async () => {
            const result = await Parse.Cloud.run('cancelEventRequest', { formId: form.id, reason: reason.trim() });
            if (!result?.success) throw new Error(result?.message || 'The request could not be cancelled.');
            await fetchForms();
        });
    };

    const destroy = (form) => {
        if (!window.confirm(`Delete "${form.title}"? This cannot be undone.`)) return;
        return run('Deleting request…', async () => {
            await ParseAPI.deleteForm(form.id);
            await fetchForms();
        });
    };

    // One button says the obvious next thing; everything else waits in the menu.
    const primaryAction = (form) => {
        if (form.status === 'Draft') return { label: 'Continue editing', onClick: () => openForm(form.id) };
        if (form.status === 'Denied') return { label: 'Fix and resubmit', onClick: () => resubmit(form) };
        if (form.isSaveTheDate) return { label: 'Build full request', onClick: () => convertSaveTheDate(form) };
        return { label: 'Open', onClick: () => openForm(form.id) };
    };

    const menuActions = (form) => {
        const items = [];
        if (form.status !== 'Draft') items.push({ label: 'Open', onClick: () => openForm(form.id) });
        items.push({ label: 'Download PDF', onClick: () => downloadPdf(form) });
        items.push({ label: 'Duplicate', onClick: () => duplicate(form) });

        if (form.status === 'Submitted' || form.status === 'Partially Approved') {
            items.push({ label: 'Remind pending approvers', onClick: () => remindApprovers(form) });
            items.push({ label: 'Resend approval emails', onClick: () => resendApprovalEmails(form) });
        }
        if (!['Draft', 'Cancelled', 'Denied'].includes(form.status)) {
            items.push({ label: 'Cancel request', onClick: () => cancel(form), danger: true });
        }
        if (['Draft', 'Fully Approved', 'Cancelled'].includes(form.status)) {
            items.push({ label: 'Delete', onClick: () => destroy(form), danger: true });
        }
        return items;
    };

    /* -------------------------------------------------------------- render */

    return (
        <>
            {busy && <LoadingOverlay message={busy} />}

            <PageHeader
                title="My requests"
                description="Every event request you've started, and where each one stands."
                actions={
                    <>
                        <Button variant="secondary" onClick={() => navigate('/home/save-the-date')}>
                            Save the date
                        </Button>
                        <Button variant="primary" onClick={() => navigate('/home/form')}>
                            New event request
                        </Button>
                    </>
                }
            >
                <div className="mx-auto flex max-w-6xl flex-col gap-3 px-4 pb-3 sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-8">
                    <div className="-mb-px flex gap-1 overflow-x-auto">
                        {FILTERS.map((f) => (
                            <button
                                key={f.key}
                                type="button"
                                onClick={() => setFilter(f.key)}
                                className={cx(
                                    'whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium transition-colors',
                                    filter === f.key
                                        ? 'border-brand-700 text-brand-800'
                                        : 'border-transparent text-neutral-500 hover:border-neutral-300 hover:text-neutral-800'
                                )}
                            >
                                {f.label}
                                <span className="ml-1.5 tabular-nums text-neutral-400">{counts[f.key] ?? 0}</span>
                            </button>
                        ))}
                    </div>
                    <div className="relative sm:w-64">
                        <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
                        <input
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search by name or location"
                            className="!pl-9"
                            aria-label="Search your requests"
                        />
                    </div>
                </div>
            </PageHeader>

            <PageBody className="space-y-4">
                {error && (
                    <Banner tone="danger" title="Something went wrong">
                        {error}
                    </Banner>
                )}

                {notice && <Banner tone="success">{notice}</Banner>}

                <Card className="overflow-hidden">
                    {loadingList ? (
                        <EmptyState title="Loading your requests…" />
                    ) : !forms.length ? (
                        <EmptyState
                            title="No requests yet"
                            description="Start an event request and it will show up here with its approval progress."
                            action={
                                <Button variant="primary" onClick={() => navigate('/home/form')}>
                                    New event request
                                </Button>
                            }
                        />
                    ) : !visible.length ? (
                        <EmptyState
                            title={filter === 'past' ? 'Nothing has aged out yet' : 'Nothing matches'}
                            description={
                                filter === 'past'
                                    ? `Requests move here once their event finished more than ${ARCHIVE_AFTER_DAYS} days ago.`
                                    : 'Try a different tab, or clear the search.'
                            }
                            action={
                                <Button
                                    onClick={() => {
                                        setFilter('all');
                                        setQuery('');
                                    }}
                                >
                                    Clear filters
                                </Button>
                            }
                        />
                    ) : (
                        <table className="min-w-full divide-y divide-neutral-200">
                            <thead className="bg-neutral-50">
                                <tr className="text-left text-xxs uppercase tracking-wider text-neutral-500">
                                    <th scope="col" className="px-5 py-2.5 font-semibold">Event</th>
                                    <th scope="col" className="px-5 py-2.5 font-semibold">Status</th>
                                    <th scope="col" className="hidden px-5 py-2.5 font-semibold lg:table-cell">Approvals</th>
                                    <th scope="col" className="hidden px-5 py-2.5 font-semibold sm:table-cell">Submitted</th>
                                    <th scope="col" className="px-5 py-2.5">
                                        <span className="sr-only">Actions</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-neutral-100">
                                {visible.map((form) => {
                                    const primary = primaryAction(form);
                                    const denial = denials[form.id];
                                    return (
                                        <tr key={form.id} className="align-top hover:bg-neutral-50/70">
                                            <td className="px-5 py-4">
                                                <button
                                                    type="button"
                                                    onClick={() => openForm(form.id)}
                                                    className="text-left text-sm font-medium text-neutral-900 hover:text-brand-800"
                                                >
                                                    {form.title}
                                                </button>
                                                {form.isSaveTheDate && (
                                                    <span className="ml-2 rounded-full bg-neutral-100 px-1.5 py-0.5 text-xxs font-medium text-neutral-600">
                                                        Save the date
                                                    </span>
                                                )}
                                                {form.isArchived && (
                                                    <span className="ml-2 rounded-full bg-neutral-100 px-1.5 py-0.5 text-xxs font-medium text-neutral-500">
                                                        Ended {form.daysSinceEvent} days ago
                                                    </span>
                                                )}
                                                <p className="mt-0.5 text-xs text-neutral-500">
                                                    {formatEventDate(form.eventDate) || 'No date set'}
                                                    {form.occurrenceCount > 1 && ` + ${form.occurrenceCount - 1} more dates`}
                                                    {form.location ? ` · ${form.location}` : ''}
                                                </p>
                                            </td>

                                            <td className="px-5 py-4">
                                                <StatusBadge status={form.status} />
                                                {form.status === 'Denied' && denial && (
                                                    <p className="mt-1.5 max-w-xs text-xs text-red-700">
                                                        {roleLabel(denial.role)}: “{denial.reason || 'No reason given'}”
                                                    </p>
                                                )}
                                                {form.status === 'Cancelled' && form.cancellationReason && (
                                                    <p className="mt-1.5 max-w-xs truncate text-xs text-neutral-500" title={form.cancellationReason}>
                                                        “{form.cancellationReason}”
                                                    </p>
                                                )}
                                            </td>

                                            <td className="hidden px-5 py-4 lg:table-cell">
                                                {['Submitted', 'Partially Approved', 'Fully Approved'].includes(form.status) ? (
                                                    <div className="space-y-1.5">
                                                        <ApprovalCount
                                                            required={form.requiredApprovals}
                                                            completed={form.completedApprovals}
                                                        />
                                                        <ApprovalRail
                                                            required={form.requiredApprovals}
                                                            completed={form.completedApprovals}
                                                        />
                                                    </div>
                                                ) : (
                                                    <span className="text-xs text-neutral-400">—</span>
                                                )}
                                            </td>

                                            <td className="hidden whitespace-nowrap px-5 py-4 text-sm text-neutral-500 sm:table-cell">
                                                {formatDate(form.createdAt)}
                                            </td>

                                            <td className="px-5 py-4">
                                                <div className="flex items-center justify-end gap-1.5">
                                                    <Button size="sm" variant="secondary" onClick={primary.onClick}>
                                                        {primary.label}
                                                    </Button>

                                                    <Menu as="div" className="relative">
                                                        <MenuButton className="rounded-md p-1.5 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-700">
                                                            <span className="sr-only">More actions for {form.title}</span>
                                                            <EllipsisHorizontalIcon className="h-5 w-5" aria-hidden="true" />
                                                        </MenuButton>
                                                        {/* anchor renders the menu in a portal, so the table
                                                            card's overflow-hidden can't clip it, and it flips
                                                            above the button when there's no room below. */}
                                                        <MenuItems
                                                            transition
                                                            anchor="bottom end"
                                                            className="z-30 w-52 rounded-md border border-neutral-200 bg-white py-1 shadow-lg [--anchor-gap:4px] focus:outline-none transition duration-100 ease-out data-[closed]:scale-95 data-[closed]:opacity-0"
                                                        >
                                                            {menuActions(form).map((action) => (
                                                                <MenuItem key={action.label}>
                                                                    <button
                                                                        type="button"
                                                                        onClick={action.onClick}
                                                                        className={cx(
                                                                            'block w-full px-4 py-2 text-left text-sm data-[focus]:bg-neutral-100',
                                                                            action.danger ? 'text-red-700' : 'text-neutral-700'
                                                                        )}
                                                                    >
                                                                        {action.label}
                                                                    </button>
                                                                </MenuItem>
                                                            ))}
                                                        </MenuItems>
                                                    </Menu>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </Card>
            </PageBody>
        </>
    );
};
